import React, { useMemo, useRef, useState } from "react";
import { Search } from "@mui/icons-material";
import { Box, Grid, InputAdornment, Link, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ColDef, GetRowIdParams, GridOptions, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { Currency } from "fond/types";
import { getTagColor } from "fond/utils/colors";
import { AgGrid } from "fond/widgets";
import { currencyValueFormatter, numberValueFormatter } from "fond/widgets/AgGrid/valueFormatter";
import GridCard from "fond/widgets/GridCard";

import { BomTreeRowData } from "../index";
import { parseTags } from "../tags";

import { TagChip } from "./BomTable.styles";

type GroupByOption = "group" | "tag" | "none";

interface BomTableProps {
  rows: BomTreeRowData[];
  partitions?: { name: string }[];
  currency?: Currency;
}

const GROUP_BY_OPTIONS: Array<{ value: GroupByOption; label: string }> = [
  { value: "group", label: "Group" },
  { value: "tag", label: "Tag" },
  { value: "none", label: "None" },
];

const TagCellRenderer: React.FC<ICellRendererParams<BomTreeRowData, string>> = ({ value }) => {
  if (!value) return null;
  const isUntagged = value === "Untagged";
  const tags = parseTags(value);
  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5} paddingY={0.5}>
      {tags.map((tag) => (
        <TagChip key={tag} size="small" label={tag} tagColor={isUntagged ? undefined : getTagColor(tag)} />
      ))}
    </Box>
  );
};

const getRowId = (params: GetRowIdParams<BomTreeRowData>) => params.data.path.join("-");

const BomTable: React.FC<BomTableProps> = ({ rows, partitions, currency }: BomTableProps) => {
  const gridRef = useRef<AgGridReact<BomTreeRowData>>(null);
  const [groupBy, setGroupBy] = useState<GroupByOption>("group");
  const [search, setSearch] = useState("");

  const columnDefs: ColDef<BomTreeRowData>[] = useMemo(() => {
    const partitionColumns: ColDef<BomTreeRowData>[] = (partitions ?? []).map(
      (partition) =>
        ({
          headerName: partition.name,
          headerClass: "ag-header-cell-center",
          children: [
            {
              colId: `${partition.name}_count`,
              field: `${partition.name}_count`,
              headerName: "Quantity",
              headerClass: "ag-right-aligned-header",
              cellClass: "vertically-align ag-right-aligned-cell",
              valueFormatter: numberValueFormatter,
              width: 110,
            },
            {
              colId: `${partition.name}_total`,
              field: `${partition.name}_total`,
              headerName: "Total",
              headerClass: "ag-right-aligned-header",
              cellClass: "vertically-align ag-right-aligned-cell",
              valueFormatter: currencyValueFormatter(currency),
              width: 120,
            },
          ] as ColDef<BomTreeRowData>[],
        }) as ColDef<BomTreeRowData>
    );

    return [
      { field: "name", headerName: "Item", hide: groupBy !== "none", wrapText: true, autoHeight: true, cellClass: "" },
      { field: "group", headerName: "Group", rowGroup: groupBy === "group", hide: groupBy === "group" },
      { field: "description", headerName: "Description", minWidth: 150, flex: 1, wrapText: true, autoHeight: true, cellClass: "" },
      { field: "unit", headerName: "Unit", width: 90 },
      {
        field: "count",
        headerName: "Quantity",
        headerClass: "ag-right-aligned-header",
        cellClass: "vertically-align ag-right-aligned-cell",
        valueFormatter: numberValueFormatter,
        width: 110,
      },
      {
        field: "unitCost",
        headerName: "Unit cost",
        headerClass: "ag-right-aligned-header",
        cellClass: "vertically-align ag-right-aligned-cell",
        valueFormatter: currencyValueFormatter(currency),
        width: 120,
      },
      ...partitionColumns,
      {
        field: "total",
        headerName: "Total cost",
        headerClass: "ag-right-aligned-header",
        cellClass: "vertically-align ag-right-aligned-cell",
        aggFunc: "sum",
        valueFormatter: currencyValueFormatter(currency),
        width: 130,
      },
      {
        field: "tag",
        headerName: "Tag",
        rowGroup: groupBy === "tag",
        hide: groupBy === "tag",
        cellRenderer: TagCellRenderer,
        wrapText: true,
        autoHeight: true,
        cellClass: "",
        width: 140,
      },
    ];
  }, [groupBy, partitions, currency]);

  const gridOptions: GridOptions<BomTreeRowData> = useMemo(
    () => ({
      getRowId,
      sideBar: false,
      rowGroupPanelShow: "never",
      groupDefaultExpanded: groupBy === "none" ? 0 : 1,
      suppressAggFuncInHeader: true,
      suppressFieldDotNotation: true,
      suppressCsvExport: true,
      suppressExcelExport: true,
      autoGroupColumnDef: {
        headerName: "Item",
        field: "name",
        minWidth: 250,
        wrapText: true,
        autoHeight: true,
        cellClass: "",
        cellRendererParams: {
          suppressCount: true,
        },
      },
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false,
        cellClass: "vertically-align",
      },
    }),
    [groupBy]
  );

  const onSearchChange = (value: string) => {
    setSearch(value);
    gridRef.current?.api?.setGridOption("quickFilterText", value);
  };

  return (
    <Grid container height="100%">
      <GridCard
        breakpoints={{ xs: 12 }}
        height="100%"
        title="Bill of materials"
        headerRightElement={
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              size="small"
              placeholder="Search items, groups, tags..."
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              sx={{ width: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Group by
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={groupBy}
                onChange={(_event, value: GroupByOption | null) => value !== null && setGroupBy(value)}
              >
                {GROUP_BY_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link component="button" variant="body2" underline="hover" onClick={() => gridRef.current?.api?.expandAll()}>
                Expand all
              </Link>
              <Typography variant="body2" color="text.secondary">
                &middot;
              </Typography>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link component="button" variant="body2" underline="hover" onClick={() => gridRef.current?.api?.collapseAll()}>
                Collapse all
              </Link>
            </Box>
          </Box>
        }
      >
        <Box flex={1} minHeight={0} display="flex" flexDirection="column">
          <AgGrid ref={gridRef} columnDefs={columnDefs} rowData={rows} gridOptions={gridOptions} variant="outlined" size="compact" />
        </Box>
      </GridCard>
    </Grid>
  );
};

export default BomTable;
